import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { userAuthService } from '../services/userAuthService';
import {
    UserIcon,
    EnvelopeIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const UserProfile: React.FC = () => {
    const { user, isAuthenticated, refreshUser } = useAuth();
    const { actualTheme } = useTheme();
    const navigate = useNavigate();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Profile form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    // Password change state
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
        }
    }, [user, isAuthenticated, navigate]);

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // TODO: Implement profile update API call
            // await userAuthService.updateProfile({ name, email });

            setSuccess('Profile updated successfully!');
            setIsEditing(false);
            await refreshUser();
        } catch (err: any) {
            setError(err.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            setLoading(false);
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        try {
            // TODO: Implement password change API call
            // await userAuthService.changePassword(currentPassword, newPassword);

            setSuccess('Password changed successfully!');
            setShowPasswordSection(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setError(err.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
            }`}>
            {/* Header */}
            <div className={`backdrop-blur-md shadow-modern sticky top-0 z-50 transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-b border-slate-600/30'
                    : 'bg-white/95 border-b border-neutral-200/50'
                }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <button
                            onClick={() => navigate('/')}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-modern text-sm font-medium border transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                                    ? 'text-slate-300 border-slate-600/30 hover:text-slate-200 hover:border-slate-500/50'
                                    : 'text-primary-600 border-primary-200 hover:text-primary-700 hover:border-primary-300'
                                }`}
                        >
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Back to Home</span>
                        </button>
                        <h1 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>My Profile</h1>
                        <div className="w-24"></div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Profile Card */}
                <div className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 ${actualTheme === 'dark'
                        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                        : 'bg-white/90 border-neutral-200/50'
                    }`}>
                    {/* Header */}
                    <div className={`px-6 py-4 border-b transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${actualTheme === 'dark' ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-700'
                                    }`}>
                                    {user.name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <h2 className={`text-xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                                        }`}>{user.name}</h2>
                                    <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                        }`}>{user.email}</p>
                                </div>
                            </div>
                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-4 py-2 rounded-modern text-sm font-medium transition-all duration-300 hover-magnify-sm modern-focus bg-primary-600 hover:bg-primary-700 text-white"
                                >
                                    Edit Profile
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Profile Form */}
                    <div className="p-6">
                        {error && (
                            <div className={`mb-4 border rounded-modern p-4 transition-colors duration-300 ${actualTheme === 'dark'
                                    ? 'bg-red-900/20 border-red-800 text-red-300'
                                    : 'bg-red-50 border-red-200 text-red-600'
                                }`}>
                                <div className="flex">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            </div>
                        )}

                        {success && (
                            <div className={`mb-4 border rounded-modern p-4 transition-colors duration-300 ${actualTheme === 'dark'
                                    ? 'bg-green-900/20 border-green-800 text-green-300'
                                    : 'bg-green-50 border-green-200 text-green-600'
                                }`}>
                                <div className="flex">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                    <span className="text-sm">{success}</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleUpdateProfile} className="space-y-6">
                            {/* Name Field */}
                            <div>
                                <label htmlFor="name" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                    }`}>
                                    Full Name
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className={`h-5 w-5 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
                                            }`} />
                                    </div>
                                    <input
                                        id="name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={!isEditing}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-modern shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                                                ? 'bg-slate-700 border-slate-600 text-white disabled:bg-slate-800 disabled:text-slate-400'
                                                : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
                                            }`}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email Field */}
                            <div>
                                <label htmlFor="email" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                    }`}>
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <EnvelopeIcon className={`h-5 w-5 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
                                            }`} />
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={!isEditing}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-modern shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${actualTheme === 'dark'
                                                ? 'bg-slate-700 border-slate-600 text-white disabled:bg-slate-800 disabled:text-slate-400'
                                                : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500'
                                            }`}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {isEditing && (
                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 py-3 px-4 border border-transparent rounded-modern shadow-sm text-sm font-medium text-white transition-all duration-300 hover-magnify-sm modern-focus bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setName(user.name || '');
                                            setEmail(user.email || '');
                                            setError(null);
                                        }}
                                        className={`flex-1 py-3 px-4 border rounded-modern shadow-sm text-sm font-medium transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                                                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Password Section */}
                    <div className={`px-6 py-4 border-t transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-gray-200'
                        }`}>
                        <button
                            onClick={() => setShowPasswordSection(!showPasswordSection)}
                            className={`w-full text-left flex items-center justify-between py-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                                }`}
                        >
                            <span className="font-medium">Change Password</span>
                            <svg
                                className={`w-5 h-5 transition-transform duration-300 ${showPasswordSection ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {showPasswordSection && (
                            <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
                                {/* Current Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                        }`}>
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <LockClosedIcon className={`h-5 w-5 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
                                                }`} />
                                        </div>
                                        <input
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className={`block w-full pl-10 pr-12 py-3 border rounded-modern ${actualTheme === 'dark'
                                                    ? 'bg-slate-700 border-slate-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showCurrentPassword ? (
                                                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* New Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                        }`}>
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <LockClosedIcon className={`h-5 w-5 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
                                                }`} />
                                        </div>
                                        <input
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className={`block w-full pl-10 pr-12 py-3 border rounded-modern ${actualTheme === 'dark'
                                                    ? 'bg-slate-700 border-slate-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showNewPassword ? (
                                                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                                        }`}>
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <LockClosedIcon className={`h-5 w-5 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
                                                }`} />
                                        </div>
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={`block w-full pl-10 pr-12 py-3 border rounded-modern ${actualTheme === 'dark'
                                                    ? 'bg-slate-700 border-slate-600 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showConfirmPassword ? (
                                                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3 px-4 border border-transparent rounded-modern shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                                >
                                    {loading ? 'Changing Password...' : 'Change Password'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
